import { useState, useMemo } from 'react'
import {
  ArrowUpDown,
  Building2,
  ChevronDown,
  Database,
  ExternalLink,
  Filter,
  MapPin,
  Search,
  TrendingUp,
  X,
  BookOpen,
  CheckCircle2,
  Clock,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAntarcticaDeals, useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import { useStageModel } from '@/hooks/useStageModel'
import { analyticsLoan } from '@/pages/liveModuleData'
import { compsService, type CompRecord } from '@/services/comps'
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
/** A comp's property type as the store holds it. Not a closed list: the page used to name seven, so a comp
 *  typed Hospitality, Self Storage or Senior Housing (the catalogue's own spellings) was left out of the
 *  filter and the market summary. The options are the types the records carry. */
type PropertyType = string

interface CompTransaction {
  id: string
  property: string
  market: string
  msa: string
  type: PropertyType
  sf: number | null
  units: number | null
  salePrice: number | null // $M
  pricePerSf: number | null
  pricePerUnit: number | null
  capRate: number | null // pct
  noi: number | null // $M
  buyer: string | null
  seller: string | null
  date: string
  /** Demo: the vendor the sample claims. Live: where the comp was recorded from. */
  source: string
  yearBuilt: number | null
  occupancy: number | null
  stories: number | null
  parkingSpaces: number | null
  address: string
  notes: string
}

/** The comps store row plus the provenance fields the API also returns. */
type StoredComp = CompRecord & {
  /** toCamelCase maps property_name → `property` and asset_type → `type`. */
  property?: string | null
  type?: string | null
  dataSource?: string | null
  sourceDocument?: string | null
  createdAt?: string | null
  notes?: string | null
}

const positiveOrNull = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null

/**
 * A row from the platform's own comps store (GET /comps/), in the page's
 * units: $MM for price and NOI, percent for cap rate and occupancy. Anything
 * the record does not hold stays null and renders as a dash.
 */
function compFromRecord(r: StoredComp): CompTransaction {
  const salePrice = positiveOrNull(r.salePrice)
  const noi = positiveOrNull(r.noiAtSale)
  const cap = positiveOrNull(r.capRate)
  const occ = positiveOrNull(r.occupancyAtSale)
  const units = positiveOrNull(r.numberOfUnits)
  return {
    id: r.id,
    property: r.propertyName || r.property || 'Unnamed comp',
    market: r.city || r.metro || '—',
    msa: r.metro || r.submarket || '—',
    type: (r.assetType || r.type || 'Unclassified') as PropertyType,
    sf: units ? null : positiveOrNull(r.unitsOrSf),
    units,
    salePrice: salePrice != null ? salePrice / 1_000_000 : null,
    pricePerSf: positiveOrNull(r.pricePerSf),
    pricePerUnit: positiveOrNull(r.pricePerUnit),
    capRate: cap != null ? (cap < 1 ? cap * 100 : cap) : null,
    noi: noi != null ? noi / 1_000_000 : null,
    buyer: null,
    seller: null,
    date: r.transactionDate ?? '',
    source: r.dataSource || 'Recorded',
    yearBuilt: positiveOrNull(r.yearBuilt),
    occupancy: occ != null ? (occ <= 1 ? occ * 100 : occ) : null,
    stories: positiveOrNull(r.stories),
    parkingSpaces: null,
    address: r.address ?? '',
    notes: [r.sourceDocument ? `Recorded from ${r.sourceDocument}.` : '', r.notes ?? ''].filter(Boolean).join(' '),
  }
}

// ---------------------------------------------------------------------------
// Mock Data — 33 Comparable Transactions
// ---------------------------------------------------------------------------
const COMP_DATA: CompTransaction[] = [
  // Multifamily (12)
  { id: 'CMP-001', property: 'Harbor Point Residences', market: 'Miami', msa: 'Miami-Fort Lauderdale', type: 'Multifamily', sf: 289000, units: 340, salePrice: 119.0, pricePerSf: null, pricePerUnit: 350000, capRate: 4.85, noi: 5.77, buyer: 'Greystar Real Estate', seller: 'Trident Residential Partners', date: '2025-11-14', source: 'CoStar', yearBuilt: 2019, occupancy: 95.2, stories: 24, parkingSpaces: 410, address: '1200 Brickell Bay Dr, Miami, FL 33131', notes: 'Class A high-rise with rooftop amenity deck and bay views.' },
  { id: 'CMP-002', property: 'Meridian Apartments', market: 'Dallas', msa: 'Dallas-Fort Worth', type: 'Multifamily', sf: 195000, units: 248, salePrice: 74.4, pricePerSf: null, pricePerUnit: 300000, capRate: 5.10, noi: 3.79, buyer: 'Cortland Partners', seller: 'Crescent Ridge Capital', date: '2025-10-22', source: 'RCA', yearBuilt: 2021, occupancy: 93.8, stories: 4, parkingSpaces: 320, address: '4500 McKinney Ave, Dallas, TX 75205', notes: 'Garden-style community with resort pool and coworking lounge.' },
  { id: 'CMP-003', property: 'The Ellington', market: 'NYC', msa: 'New York-Newark', type: 'Multifamily', sf: 162000, units: 204, salePrice: 81.6, pricePerSf: null, pricePerUnit: 400000, capRate: 4.42, noi: 3.61, buyer: 'Brookfield Asset Mgmt', seller: 'Lightstone Group', date: '2025-09-05', source: 'CoStar', yearBuilt: 2018, occupancy: 96.1, stories: 32, parkingSpaces: 150, address: '101 W 14th St, New York, NY 10011', notes: 'Luxury mid-block tower in West Village, amenity-rich.' },
  { id: 'CMP-004', property: 'Skyline Lofts', market: 'Denver', msa: 'Denver-Aurora', type: 'Multifamily', sf: 178000, units: 220, salePrice: 66.0, pricePerSf: null, pricePerUnit: 300000, capRate: 5.25, noi: 3.47, buyer: 'UDR Inc.', seller: 'Alliance Residential', date: '2025-08-18', source: 'Internal', yearBuilt: 2020, occupancy: 92.4, stories: 6, parkingSpaces: 275, address: '2200 Blake St, Denver, CO 80205', notes: 'RiNo district adaptive reuse, heavy millennial draw.' },
  { id: 'CMP-005', property: 'Peachtree Commons', market: 'Atlanta', msa: 'Atlanta-Sandy Springs', type: 'Multifamily', sf: 210000, units: 312, salePrice: 79.6, pricePerSf: null, pricePerUnit: 255000, capRate: 5.45, noi: 4.34, buyer: 'MAA Communities', seller: 'Wood Partners', date: '2025-07-02', source: 'Broker', yearBuilt: 2022, occupancy: 94.6, stories: 5, parkingSpaces: 390, address: '1155 Peachtree St NE, Atlanta, GA 30309', notes: 'Midtown high-rise with skyline views and ground-floor retail.' },
  { id: 'CMP-006', property: 'Sunset Pointe', market: 'Phoenix', msa: 'Phoenix-Mesa', type: 'Multifamily', sf: 245000, units: 296, salePrice: 88.8, pricePerSf: null, pricePerUnit: 300000, capRate: 5.15, noi: 4.57, buyer: 'Invitation Homes', seller: 'Mark-Taylor Residential', date: '2025-06-11', source: 'CoStar', yearBuilt: 2021, occupancy: 95.0, stories: 3, parkingSpaces: 380, address: '7200 E Camelback Rd, Scottsdale, AZ 85251', notes: 'Suburban luxury community with five pools and mountain views.' },
  { id: 'CMP-007', property: 'Lake Union Tower', market: 'Seattle', msa: 'Seattle-Tacoma', type: 'Multifamily', sf: 155000, units: 198, salePrice: 75.2, pricePerSf: null, pricePerUnit: 380000, capRate: 4.60, noi: 3.46, buyer: 'AvalonBay Communities', seller: 'Holland Residential', date: '2025-05-20', source: 'RCA', yearBuilt: 2020, occupancy: 93.2, stories: 18, parkingSpaces: 210, address: '333 Dexter Ave N, Seattle, WA 98109', notes: 'South Lake Union tower adjacent to Amazon HQ campus.' },
  { id: 'CMP-008', property: 'Riverfront Place', market: 'Chicago', msa: 'Chicago-Naperville', type: 'Multifamily', sf: 230000, units: 276, salePrice: 71.8, pricePerSf: null, pricePerUnit: 260000, capRate: 5.35, noi: 3.84, buyer: 'Equity Residential', seller: 'Fifield Companies', date: '2025-04-15', source: 'CoStar', yearBuilt: 2019, occupancy: 91.8, stories: 22, parkingSpaces: 300, address: '400 N Wells St, Chicago, IL 60654', notes: 'River North location, rooftop with skyline view.' },
  { id: 'CMP-009', property: 'Uptown Heights', market: 'Dallas', msa: 'Dallas-Fort Worth', type: 'Multifamily', sf: 185000, units: 232, salePrice: 85.7, pricePerSf: null, pricePerUnit: 369400, capRate: 4.90, noi: 4.20, buyer: 'Camden Property Trust', seller: 'JPI Construction', date: '2025-03-28', source: 'Internal', yearBuilt: 2023, occupancy: 96.4, stories: 8, parkingSpaces: 290, address: '3300 McKinney Ave, Dallas, TX 75204', notes: 'Best-in-class Uptown asset, lease-up outperformance.' },
  { id: 'CMP-010', property: 'Pacific Gardens', market: 'LA', msa: 'Los Angeles-Long Beach', type: 'Multifamily', sf: 198000, units: 260, salePrice: 98.8, pricePerSf: null, pricePerUnit: 380000, capRate: 4.55, noi: 4.50, buyer: 'Essex Property Trust', seller: 'Caruso Affiliated', date: '2025-02-10', source: 'RCA', yearBuilt: 2022, occupancy: 94.8, stories: 7, parkingSpaces: 340, address: '8800 Beverly Blvd, Los Angeles, CA 90048', notes: 'Beverly Grove location with high walk score.' },
  { id: 'CMP-011', property: 'The Morrison', market: 'Boston', msa: 'Boston-Cambridge', type: 'Multifamily', sf: 142000, units: 186, salePrice: 69.8, pricePerSf: null, pricePerUnit: 375300, capRate: 4.70, noi: 3.28, buyer: 'Cabot Properties', seller: 'National Development', date: '2025-01-22', source: 'Broker', yearBuilt: 2020, occupancy: 95.6, stories: 14, parkingSpaces: 195, address: '100 Seaport Blvd, Boston, MA 02210', notes: 'Seaport District tower with harbor views and transit access.' },
  { id: 'CMP-012', property: 'Coral Springs Village', market: 'Miami', msa: 'Miami-Fort Lauderdale', type: 'Multifamily', sf: 275000, units: 384, salePrice: 107.5, pricePerSf: null, pricePerUnit: 279900, capRate: 5.30, noi: 5.70, buyer: 'Starwood Capital Group', seller: 'Related Group', date: '2024-12-08', source: 'CoStar', yearBuilt: 2017, occupancy: 93.0, stories: 4, parkingSpaces: 480, address: '9500 NW 41st St, Coral Springs, FL 33065', notes: 'Suburban garden-style, 2017 vintage with value-add potential.' },

  // Office (6)
  { id: 'CMP-013', property: 'Pinnacle Tower', market: 'Dallas', msa: 'Dallas-Fort Worth', type: 'Office', sf: 425000, units: null, salePrice: 212.5, pricePerSf: 500, pricePerUnit: null, capRate: 6.20, noi: 13.18, buyer: 'KBS Real Estate', seller: 'Lone Star Office Holdings', date: '2025-10-01', source: 'CoStar', yearBuilt: 2016, occupancy: 88.4, stories: 35, parkingSpaces: 1200, address: '1700 Pacific Ave, Dallas, TX 75201', notes: 'Class A tower in Arts District, recently renovated lobby.' },
  { id: 'CMP-014', property: 'Figueroa Center', market: 'LA', msa: 'Los Angeles-Long Beach', type: 'Office', sf: 380000, units: null, salePrice: 209.0, pricePerSf: 550, pricePerUnit: null, capRate: 5.80, noi: 12.12, buyer: 'Brookfield DTLA', seller: 'Maguire Properties', date: '2025-08-14', source: 'RCA', yearBuilt: 2015, occupancy: 86.2, stories: 42, parkingSpaces: 900, address: '801 S Figueroa St, Los Angeles, CA 90017', notes: 'DTLA trophy tower, LEED Gold, significant tenant rollover risk.' },
  { id: 'CMP-015', property: 'Wacker Drive Tower', market: 'Chicago', msa: 'Chicago-Naperville', type: 'Office', sf: 520000, units: null, salePrice: 182.0, pricePerSf: 350, pricePerUnit: null, capRate: 6.80, noi: 12.38, buyer: 'Columbia Property Trust', seller: 'Tishman Speyer', date: '2025-06-22', source: 'CoStar', yearBuilt: 2008, occupancy: 82.6, stories: 45, parkingSpaces: 800, address: '71 S Wacker Dr, Chicago, IL 60606', notes: 'Class A Chicago Loop tower. Below replacement cost.' },
  { id: 'CMP-016', property: 'Midtown Plaza', market: 'NYC', msa: 'New York-Newark', type: 'Office', sf: 310000, units: null, salePrice: 186.0, pricePerSf: 600, pricePerUnit: null, capRate: 5.50, noi: 10.23, buyer: 'SL Green Realty', seller: 'RXR Realty', date: '2025-04-30', source: 'Internal', yearBuilt: 2014, occupancy: 91.2, stories: 28, parkingSpaces: null, address: '1350 Broadway, New York, NY 10018', notes: 'Midtown South creative office, strong tech tenant base.' },
  { id: 'CMP-017', property: 'One Congress', market: 'Boston', msa: 'Boston-Cambridge', type: 'Office', sf: 290000, units: null, salePrice: 130.5, pricePerSf: 450, pricePerUnit: null, capRate: 6.10, noi: 7.96, buyer: 'Boston Properties', seller: 'Oxford Properties', date: '2025-02-28', source: 'Broker', yearBuilt: 2012, occupancy: 89.0, stories: 18, parkingSpaces: 350, address: '1 Congress St, Boston, MA 02114', notes: 'Financial District core, needs capital for lobby modernization.' },
  { id: 'CMP-018', property: 'Buckhead Financial Center', market: 'Atlanta', msa: 'Atlanta-Sandy Springs', type: 'Office', sf: 350000, units: null, salePrice: 140.0, pricePerSf: 400, pricePerUnit: null, capRate: 6.50, noi: 9.10, buyer: 'Cousins Properties', seller: 'Highwoods Properties', date: '2024-12-15', source: 'RCA', yearBuilt: 2010, occupancy: 85.8, stories: 22, parkingSpaces: 650, address: '3344 Peachtree Rd NE, Atlanta, GA 30326', notes: 'Buckhead submarket with strong credit tenants.' },

  // Industrial (6)
  { id: 'CMP-019', property: 'Ridge Logistics Center', market: 'Dallas', msa: 'Dallas-Fort Worth', type: 'Industrial', sf: 1250000, units: null, salePrice: 187.5, pricePerSf: 150, pricePerUnit: null, capRate: 5.10, noi: 9.56, buyer: 'Prologis Inc.', seller: 'Blue Mesa Logistics', date: '2025-11-08', source: 'CoStar', yearBuilt: 2022, occupancy: 97.5, stories: 1, parkingSpaces: 200, address: '4100 Mountain Creek Pkwy, Dallas, TX 75236', notes: 'Last-mile distribution, 36-ft clear height, cross-dock.' },
  { id: 'CMP-020', property: 'Inland Empire DC', market: 'LA', msa: 'Los Angeles-Long Beach', type: 'Industrial', sf: 980000, units: null, salePrice: 196.0, pricePerSf: 200, pricePerUnit: null, capRate: 4.80, noi: 9.41, buyer: 'GLP Capital Partners', seller: 'Duke Realty', date: '2025-09-12', source: 'RCA', yearBuilt: 2023, occupancy: 100.0, stories: 1, parkingSpaces: 300, address: '14200 Meridian Pkwy, Riverside, CA 92518', notes: 'Brand-new Class A DC with full Amazon NNN lease.' },
  { id: 'CMP-021', property: 'O\'Hare Logistics Park', market: 'Chicago', msa: 'Chicago-Naperville', type: 'Industrial', sf: 850000, units: null, salePrice: 110.5, pricePerSf: 130, pricePerUnit: null, capRate: 5.60, noi: 6.19, buyer: 'STAG Industrial', seller: 'CenterPoint Properties', date: '2025-07-19', source: 'Internal', yearBuilt: 2019, occupancy: 95.8, stories: 1, parkingSpaces: 180, address: '10400 W Touhy Ave, Rosemont, IL 60018', notes: 'Near O\'Hare airport, multi-tenant with HVAC and dock-high.' },
  { id: 'CMP-022', property: 'Sky Harbor Industrial', market: 'Phoenix', msa: 'Phoenix-Mesa', type: 'Industrial', sf: 720000, units: null, salePrice: 100.8, pricePerSf: 140, pricePerUnit: null, capRate: 5.30, noi: 5.34, buyer: 'Rexford Industrial', seller: 'VanTrust Real Estate', date: '2025-05-05', source: 'CoStar', yearBuilt: 2021, occupancy: 98.2, stories: 1, parkingSpaces: 160, address: '2800 S 67th Ave, Phoenix, AZ 85043', notes: 'Airport-adjacent distribution with cold storage capacity.' },
  { id: 'CMP-023', property: 'Port Commerce Center', market: 'Miami', msa: 'Miami-Fort Lauderdale', type: 'Industrial', sf: 560000, units: null, salePrice: 89.6, pricePerSf: 160, pricePerUnit: null, capRate: 5.20, noi: 4.66, buyer: 'Terreno Realty', seller: 'Flagler Global Logistics', date: '2025-03-10', source: 'Broker', yearBuilt: 2020, occupancy: 96.4, stories: 1, parkingSpaces: 140, address: '11600 NW 36th St, Doral, FL 33178', notes: 'Near MIA airport and PortMiami, cold and dry mix.' },
  { id: 'CMP-024', property: 'Cascade Fulfillment Hub', market: 'Seattle', msa: 'Seattle-Tacoma', type: 'Industrial', sf: 640000, units: null, salePrice: 115.2, pricePerSf: 180, pricePerUnit: null, capRate: 4.90, noi: 5.64, buyer: 'Clarion Partners', seller: 'Panattoni Development', date: '2025-01-15', source: 'RCA', yearBuilt: 2022, occupancy: 99.0, stories: 1, parkingSpaces: 190, address: '18500 International Blvd, SeaTac, WA 98188', notes: 'Single-tenant FedEx Ground facility, long-term NNN.' },

  // Retail (4)
  { id: 'CMP-025', property: 'Union Retail Galleria', market: 'Chicago', msa: 'Chicago-Naperville', type: 'Retail', sf: 185000, units: null, salePrice: 55.5, pricePerSf: 300, pricePerUnit: null, capRate: 6.80, noi: 3.77, buyer: 'Federal Realty', seller: 'Apex Retail Group', date: '2025-09-28', source: 'CoStar', yearBuilt: 2014, occupancy: 91.5, stories: 2, parkingSpaces: 600, address: '1555 N Clybourn Ave, Chicago, IL 60610', notes: 'Grocery-anchored lifestyle center with strong foot traffic.' },
  { id: 'CMP-026', property: 'Legacy West Village', market: 'Dallas', msa: 'Dallas-Fort Worth', type: 'Retail', sf: 220000, units: null, salePrice: 99.0, pricePerSf: 450, pricePerUnit: null, capRate: 5.90, noi: 5.84, buyer: 'Simon Property Group', seller: 'KDC Real Estate', date: '2025-07-14', source: 'RCA', yearBuilt: 2017, occupancy: 94.8, stories: 3, parkingSpaces: 1100, address: '7700 Windrose Ave, Plano, TX 75024', notes: 'Plano mixed-use lifestyle destination, JCPenney HQ adjacent.' },
  { id: 'CMP-027', property: 'Wynwood Shops', market: 'Miami', msa: 'Miami-Fort Lauderdale', type: 'Retail', sf: 78000, units: null, salePrice: 35.1, pricePerSf: 450, pricePerUnit: null, capRate: 5.60, noi: 1.97, buyer: 'Jamestown LP', seller: 'L Real Estate', date: '2025-04-20', source: 'Internal', yearBuilt: 2019, occupancy: 97.2, stories: 2, parkingSpaces: 200, address: '2600 NW 2nd Ave, Miami, FL 33127', notes: 'Wynwood arts district, F&B-heavy tenant roster.' },
  { id: 'CMP-028', property: 'Santa Monica Place', market: 'LA', msa: 'Los Angeles-Long Beach', type: 'Retail', sf: 140000, units: null, salePrice: 63.0, pricePerSf: 450, pricePerUnit: null, capRate: 6.10, noi: 3.84, buyer: 'Macerich Company', seller: 'GPI Companies', date: '2025-02-05', source: 'CoStar', yearBuilt: 2012, occupancy: 88.0, stories: 3, parkingSpaces: 800, address: '395 Santa Monica Pl, Santa Monica, CA 90401', notes: 'Open-air coastal retail with luxury tenant mix.' },

  // Hotel (3)
  { id: 'CMP-029', property: 'The Riviera Miami Beach', market: 'Miami', msa: 'Miami-Fort Lauderdale', type: 'Hotel', sf: 195000, units: 280, salePrice: 154.0, pricePerSf: null, pricePerUnit: 550000, capRate: 6.40, noi: 9.86, buyer: 'DiamondRock Hospitality', seller: 'Aman Group', date: '2025-10-06', source: 'CoStar', yearBuilt: 2018, occupancy: 78.5, stories: 15, parkingSpaces: 320, address: '4441 Collins Ave, Miami Beach, FL 33140', notes: 'Beachfront luxury resort, $620 ADR, RevPAR leader.' },
  { id: 'CMP-030', property: 'Millennium Tower Hotel', market: 'NYC', msa: 'New York-Newark', type: 'Hotel', sf: 165000, units: 220, salePrice: 132.0, pricePerSf: null, pricePerUnit: 600000, capRate: 5.90, noi: 7.79, buyer: 'RLJ Lodging Trust', seller: 'Anbang Insurance', date: '2025-06-30', source: 'RCA', yearBuilt: 2015, occupancy: 82.1, stories: 28, parkingSpaces: null, address: '145 W 44th St, New York, NY 10036', notes: 'Times Square select-service, strong corporate demand.' },
  { id: 'CMP-031', property: 'Scottsdale Resort & Spa', market: 'Phoenix', msa: 'Phoenix-Mesa', type: 'Hotel', sf: 310000, units: 340, salePrice: 170.0, pricePerSf: null, pricePerUnit: 500000, capRate: 6.20, noi: 10.54, buyer: 'Pebblebrook Hotel Trust', seller: 'Montage Resorts', date: '2025-03-22', source: 'Broker', yearBuilt: 2016, occupancy: 71.8, stories: 4, parkingSpaces: 500, address: '6000 E Camelback Rd, Scottsdale, AZ 85251', notes: 'Full-service luxury resort with championship golf course.' },

  // Mixed-Use (2)
  { id: 'CMP-032', property: 'Seaport Mixed-Use', market: 'Seattle', msa: 'Seattle-Tacoma', type: 'Mixed-Use', sf: 340000, units: 180, salePrice: 136.0, pricePerSf: 400, pricePerUnit: null, capRate: 5.40, noi: 7.34, buyer: 'Hines Interests', seller: 'Harbor Capital LLC', date: '2025-08-02', source: 'Internal', yearBuilt: 2021, occupancy: 92.0, stories: 22, parkingSpaces: 250, address: '1201 Western Ave, Seattle, WA 98101', notes: 'Waterfront tower with 180 residential + 40K SF ground-floor retail.' },
  { id: 'CMP-033', property: 'Midtown East Commons', market: 'Atlanta', msa: 'Atlanta-Sandy Springs', type: 'Mixed-Use', sf: 280000, units: 210, salePrice: 94.5, pricePerSf: 338, pricePerUnit: null, capRate: 5.70, noi: 5.39, buyer: 'Jamestown LP', seller: 'Carter Funds', date: '2025-05-12', source: 'CoStar', yearBuilt: 2020, occupancy: 90.5, stories: 16, parkingSpaces: 320, address: '725 Ponce de Leon Ave NE, Atlanta, GA 30306', notes: 'Poncey-Highland mixed-use, walkable to BeltLine.' },

  // Lab/R&D (2)
  { id: 'CMP-034', property: 'Kendall Square Labs', market: 'Boston', msa: 'Boston-Cambridge', type: 'Lab/R&D', sf: 240000, units: null, salePrice: 216.0, pricePerSf: 900, pricePerUnit: null, capRate: 5.00, noi: 10.80, buyer: 'Alexandria Real Estate', seller: 'MIT Investment Mgmt', date: '2025-09-18', source: 'RCA', yearBuilt: 2020, occupancy: 98.0, stories: 8, parkingSpaces: 280, address: '610 Main St, Cambridge, MA 02142', notes: 'Kendall Square GMP-ready lab space, 100% NNN, biotech tenants.' },
  { id: 'CMP-035', property: 'Torrey Pines Science Park', market: 'LA', msa: 'Los Angeles-Long Beach', type: 'Lab/R&D', sf: 180000, units: null, salePrice: 144.0, pricePerSf: 800, pricePerUnit: null, capRate: 5.20, noi: 7.49, buyer: 'Healthpeak Properties', seller: 'BioMed Realty Trust', date: '2025-04-08', source: 'CoStar', yearBuilt: 2019, occupancy: 96.5, stories: 4, parkingSpaces: 350, address: '10933 N Torrey Pines Rd, La Jolla, CA 92037', notes: 'San Diego life science corridor, adjacent to Salk Institute.' },
]

// ---------------------------------------------------------------------------
// Derived constants
// ---------------------------------------------------------------------------
function fmt$(n: number | null) {
  if (n == null) return '—'
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}B`
  return `$${n.toFixed(1)}M`
}

function fmtNum(n: number) {
  return n.toLocaleString('en-US')
}

function fmtPct(n: number | null) {
  return n == null ? '—' : `${n.toFixed(2)}%`
}

type SortKey = keyof CompTransaction
type SortDir = 'asc' | 'desc'

// ---------------------------------------------------------------------------
// Market Summary row type
// ---------------------------------------------------------------------------
interface MarketSummaryRow {
  type: PropertyType
  count: number
  avgCapRate: number | null
  avgPriceSf: number | null
  avgPriceUnit: number | null
  totalVolume: number
  yoyChange: string | null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function CompDatabase() {
  const demoEnabled = useModuleDemoEnabled('comp-database')
  const rawDeals = useAntarcticaDeals('comp-database')
  // "Funded" is the served model's terminal stage and nothing else: the KPI and
  // every row's badge ask the same definition.
  const stageModel = useStageModel()
  // Live loans are in dollars with nullable LTV/DSCR; the Our Deals view was
  // written for the curated $MM set and threw on the first null DSCR.
  const deals = useMemo(
    () => (demoEnabled ? rawDeals : rawDeals.map(analyticsLoan)),
    [demoEnabled, rawDeals],
  )
  // Market Comps: the demo sample, or the platform's own comps store. The
  // 35 hardcoded "CoStar / RCA" transactions used to render on live accounts
  // with "auto-refresh daily" while no vendor was connected (MOD-02).
  const compsQuery = useQuery({
    queryKey: ['comps', 'all'],
    queryFn: () => compsService.search({}),
    enabled: !demoEnabled,
    staleTime: 60_000,
  })
  const comps: CompTransaction[] = useMemo(
    () => (demoEnabled ? COMP_DATA : (compsQuery.data ?? []).map((r) => compFromRecord(r as StoredComp))),
    [demoEnabled, compsQuery.data],
  )
  const ALL_MARKETS = useMemo(() => [...new Set(comps.map((c) => c.market))].sort(), [comps])
  // The property types the records carry — the filter and the market summary cover every one of them.
  const ALL_TYPES: PropertyType[] = useMemo(
    () => [...new Set([...comps.map((c) => c.type), ...deals.map((d) => d.type)].filter(Boolean))].sort(),
    [comps, deals],
  )
  const lastRecorded = useMemo(() => {
    const stamps = (compsQuery.data ?? [])
      .map((r) => (r as StoredComp).createdAt ?? '')
      .filter(Boolean)
      .sort()
    return stamps.length ? stamps[stamps.length - 1].slice(0, 10) : null
  }, [compsQuery.data])
  const [compView, setCompView] = useState<'market' | 'our-deals'>('market')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<PropertyType | ''>('')
  const [marketFilter, setMarketFilter] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [selectedComp, setSelectedComp] = useState<CompTransaction | null>(null)

  // "Our Deals" view — deals filtered by search/type
  const ourDealsFiltered = useMemo(() => {
    let data = deals.map(d => ({ ...d, isOurDeal: true }))
    if (search) {
      const q = search.toLowerCase()
      data = data.filter(d => d.property.toLowerCase().includes(q) || d.borrower.toLowerCase().includes(q) || d.location.toLowerCase().includes(q))
    }
    if (typeFilter) data = data.filter(d => d.type === typeFilter)
    return data
  }, [deals, search, typeFilter])

  // Filter logic
  const filtered = useMemo(() => {
    let data = [...comps]
    if (search) {
      const q = search.toLowerCase()
      data = data.filter(
        (c) =>
          c.property.toLowerCase().includes(q) ||
          (c.buyer ?? '').toLowerCase().includes(q) ||
          (c.seller ?? '').toLowerCase().includes(q) ||
          c.market.toLowerCase().includes(q)
      )
    }
    if (typeFilter) data = data.filter((c) => c.type === typeFilter)
    if (marketFilter) data = data.filter((c) => c.market === marketFilter)

    data.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'string' && typeof bv === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av
      return 0
    })
    return data
  }, [comps, search, typeFilter, marketFilter, sortKey, sortDir])

  // Metrics — over the comps that record each figure; null when none do.
  const avgOf = (vals: Array<number | null>) => {
    const known = vals.filter((v): v is number => v != null)
    return known.length ? known.reduce((s, v) => s + v, 0) / known.length : null
  }
  const totalComps = comps.length
  const avgCapRate = avgOf(comps.map((c) => c.capRate))
  // $/SF over the comps sized in square feet (the KPI's "office, industrial,
  // retail"); a unit-counted comp is represented by its $/unit instead.
  const avgPsf = avgOf(comps.filter((c) => !c.units).map((c) => c.pricePerSf))
  const avgPpu = avgOf(comps.map((c) => c.pricePerUnit))
  const marketsCovered = new Set(comps.map((c) => c.market)).size

  // Our Deals: a Triage deal is not "evaluated & funded"; and "states" counts
  // states — the last comma part counted "TX 75093" and "Plano, TX" apart.
  const ourFundedCount = deals.filter((d) => stageModel.isFunded(d.stage)).length
  const ourStates = new Set(
    deals
      .map((d) => /(?:^|[\s,])([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/.exec((d.location ?? '').trim())?.[1])
      .filter((st): st is string => Boolean(st)),
  )

  // Market summary
  const marketSummary: MarketSummaryRow[] = useMemo(() => {
    return ALL_TYPES.map((type) => {
      const subset = comps.filter((c) => c.type === type)
      if (!subset.length) return null
      const withCap = subset.filter((c) => c.capRate != null)
      const avgCr = withCap.length ? withCap.reduce((s, c) => s + (c.capRate ?? 0), 0) / withCap.length : null
      // As the KPI: a unit-counted comp is priced per unit, never per foot
      // (Multifamily read "$364/SF" while the tile excluded it).
      const sfItems = subset.filter((c) => c.pricePerSf != null && !c.units)
      const unitItems = subset.filter((c) => c.pricePerUnit != null)
      const avgSf = sfItems.length ? sfItems.reduce((s, c) => s + (c.pricePerSf ?? 0), 0) / sfItems.length : null
      const avgUnit = unitItems.length ? unitItems.reduce((s, c) => s + (c.pricePerUnit ?? 0), 0) / unitItems.length : null
      const volume = subset.reduce((s, c) => s + (c.salePrice ?? 0), 0)
      const yoyChanges: Record<string, string> = {
        Multifamily: '-2.4%',
        Office: '-8.1%',
        Industrial: '+3.2%',
        Retail: '-1.8%',
        Hotel: '+1.5%',
        'Mixed-Use': '-0.6%',
        'Lab/R&D': '+5.8%',
      }
      // The YoY moves are sample figures; the comps store has no time series.
      return { type, count: subset.length, avgCapRate: avgCr, avgPriceSf: avgSf, avgPriceUnit: avgUnit, totalVolume: volume, yoyChange: demoEnabled ? (yoyChanges[type] ?? null) : null }
    }).filter(Boolean) as MarketSummaryRow[]
  }, [comps, demoEnabled, ALL_TYPES])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th
      className="cursor-pointer select-none px-3 py-1.5 text-left hover:text-amber-300"
      onClick={() => handleSort(field)}
      style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.16em', textTransform: 'uppercase' as const, color: sortKey === field ? '#d9b768' : '#8c8c8c', borderBottom: '1px solid var(--line-soft)', background: 'rgba(0,0,0,0.25)' }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === field && <ArrowUpDown size={9} className="text-amber-400" />}
      </span>
    </th>
  )

  const similarPipelineCount = useMemo(() => {
    if (!selectedComp) return 0
    return deals.filter(
      (d) => d.type === selectedComp.type || d.location.toLowerCase().includes(selectedComp.market.toLowerCase()),
    ).length
  }, [selectedComp, deals])

  return (
    <div className="page-fill gap-2 p-3">
      {/* View toggle */}
      <div className="flex items-center gap-1 border-b border-amber-400/10 pb-2">
        <button
          onClick={() => setCompView('market')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border transition-colors ${compView === 'market' ? 'border-amber-400/30 bg-amber-400/10 text-amber-300' : 'border-transparent text-stone-500 hover:text-stone-300'}`}
        >
          <Database size={11} /> Market Comps
        </button>
        <button
          onClick={() => setCompView('our-deals')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border transition-colors ${compView === 'our-deals' ? 'border-amber-400/30 bg-amber-400/10 text-amber-300' : 'border-transparent text-stone-500 hover:text-stone-300'}`}
        >
          <BookOpen size={11} /> Our Deals
        </button>
        <span className="ml-3 text-[10px] text-stone-600">{compView === 'our-deals' ? 'Prior evaluated and funded deals — searchable by geography and asset type' : demoEnabled ? 'Sample data — 35 illustrative transactions, no vendor feed' : `${comps.length} comp${comps.length === 1 ? '' : 's'} recorded from uploaded packages · no market-data vendor (CoStar, RCA) connected`}</span>
      </div>

      {/* Our Deals View */}
      {compView === 'our-deals' && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <section className="grid flex-shrink-0 gap-2 md:grid-cols-3 xl:grid-cols-6">
            {[
              { label: 'Our Deal Comps', value: String(deals.length), sub: `${ourFundedCount} funded · ${deals.length - ourFundedCount} in pipeline` },
              { label: 'Avg LTV', value: ((v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`))(avgOf(deals.map((d) => (d.ltv == null ? null : d.ltv <= 1 ? d.ltv : d.ltv / 100)))), sub: 'deals with LTV on record' },
              { label: 'Avg DSCR', value: ((v) => (v == null ? '—' : `${v.toFixed(2)}x`))(avgOf(deals.map((d) => d.dscr ?? null))), sub: 'deals with DSCR on record' },
              { label: 'Avg Loan Size', value: deals.length ? `$${(deals.reduce((s,d) => s + d.loanAmount, 0) / deals.length).toFixed(1)}M` : '—', sub: 'all stages' },
              { label: 'Asset Types', value: String(new Set(deals.map(d=>d.type)).size), sub: 'covered' },
              { label: 'Markets', value: String(ourStates.size), sub: ourStates.size === 1 ? 'state' : 'states' },
            ].map(m => (
              <div key={m.label} className="card px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-stone-600">{m.label}</div>
                <div className="mt-2 text-xl font-semibold text-stone-100">{m.value}</div>
                <div className="mt-1 text-[11px] text-stone-500">{m.sub}</div>
              </div>
            ))}
          </section>
          <section className="card flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-amber-400/10 px-4 py-2">
              <BookOpen size={14} className="text-amber-400" />
              <span className="terminal-kicker">Our Evaluated & Funded Deals</span>
              <div className="ml-auto flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1.5">
                  <Search size={12} className="text-stone-600" />
                  <input
                    type="text"
                    placeholder="Search property, borrower, market..."
                    className="w-44 bg-transparent text-[11px] text-stone-300 placeholder:text-stone-600 outline-none"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className="appearance-none rounded-md border border-amber-400/10 bg-black/40 py-1.5 pr-7 pl-2.5 text-[11px] text-stone-300 outline-none"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as PropertyType | '')}
                >
                  <option value="">All Types</option>
                  {ALL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="border-b border-amber-400/10">
                    {['Deal ID', 'Property', 'Type', 'Location', 'Loan Amount', 'LTV', 'DSCR', 'Debt Yield', 'Stage', 'Outcome'].map(col => (
                      <th key={col} className="px-3 py-2 text-left text-[9px] font-medium uppercase tracking-[0.16em] text-stone-600 bg-black/25">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ourDealsFiltered.map(deal => {
                    const ltv = deal.ltv == null ? null : deal.ltv <= 1 ? deal.ltv : deal.ltv / 100
                    const isFunded = stageModel.isFunded(deal.stage)
                    const ltvColor = ltv == null ? 'text-stone-500' : ltv > 0.78 ? 'text-red-400' : ltv > 0.70 ? 'text-amber-300' : 'text-emerald-400'
                    const dscrColor = deal.dscr == null ? 'text-stone-500' : deal.dscr < 1.15 ? 'text-red-400' : deal.dscr < 1.30 ? 'text-amber-300' : 'text-emerald-400'
                    return (
                      <tr key={deal.id} className="border-b border-amber-400/6 hover:bg-white/[0.015] transition-colors">
                        <td className="px-3 py-2 text-[11px] font-mono text-stone-500">{deal.id}</td>
                        <td className="px-3 py-2 text-xs text-amber-300">{deal.property}</td>
                        <td className="px-3 py-2 text-xs text-stone-400">{deal.type}</td>
                        <td className="px-3 py-2 text-xs text-stone-400">{deal.location}</td>
                        <td className="px-3 py-2 text-xs font-mono text-stone-200">${deal.loanAmount.toFixed(1)}M</td>
                        <td className={`px-3 py-2 text-xs font-mono ${ltvColor}`}>{ltv == null ? '—' : `${(ltv * 100).toFixed(0)}%`}</td>
                        <td className={`px-3 py-2 text-xs font-mono ${dscrColor}`}>{deal.dscr == null ? '—' : `${deal.dscr.toFixed(2)}x`}</td>
                        <td className="px-3 py-2 text-xs font-mono text-stone-400">{deal.debtYield ? `${(deal.debtYield * 100).toFixed(1)}%` : '—'}</td>
                        <td className="px-3 py-2 text-xs text-stone-400">{deal.stage}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${isFunded ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {isFunded ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                            {isFunded ? 'Funded' : 'In Pipeline'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {compView === 'market' && (
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      {/* Metric Strip */}
      <section className="grid flex-shrink-0 gap-2 md:grid-cols-3 xl:grid-cols-6">
        {[
          { label: 'Total Comps', value: String(totalComps), sub: 'in database' },
          { label: 'Avg Cap Rate', value: fmtPct(avgCapRate), sub: 'all property types' },
          { label: 'Avg $/SF', value: avgPsf == null ? '—' : `$${Math.round(avgPsf).toLocaleString()}`, sub: 'office, industrial, retail' },
          { label: 'Avg $/Unit', value: avgPpu == null ? '—' : `$${Math.round(avgPpu).toLocaleString()}`, sub: 'multifamily, hotel' },
          { label: 'Markets Covered', value: String(marketsCovered), sub: demoEnabled ? 'major MSAs' : 'cities' },
          demoEnabled
            ? { label: 'Last Updated', value: '—', sub: 'sample data, no feed' }
            : { label: 'Last Recorded', value: lastRecorded ?? '—', sub: 'added from packages, no vendor feed' },
        ].map((m) => (
          <div key={m.label} className="card px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-600">{m.label}</div>
            <div className="mt-2 text-xl font-semibold text-stone-100">{m.value}</div>
            <div className="mt-1 text-[11px] text-stone-500">{m.sub}</div>
          </div>
        ))}
      </section>

      {/* Filter Bar */}
      <section className="card flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="flex items-center gap-1.5 text-stone-500">
          <Filter size={13} />
          <span className="text-[10px] uppercase tracking-[0.18em]">Filters</span>
        </div>

        <div className="flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1.5">
          <Search size={12} className="text-stone-600" />
          <input
            type="text"
            placeholder="Search property, buyer, seller..."
            className="w-48 bg-transparent text-[11px] text-stone-300 placeholder:text-stone-600 outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="relative">
          <select
            className="appearance-none rounded-md border border-amber-400/10 bg-black/40 py-1.5 pr-7 pl-2.5 text-[11px] text-stone-300 outline-none focus:border-amber-400/30"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as PropertyType | '')}
          >
            <option value="">All Types</option>
            {ALL_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stone-600" />
        </div>

        <div className="relative">
          <select
            className="appearance-none rounded-md border border-amber-400/10 bg-black/40 py-1.5 pr-7 pl-2.5 text-[11px] text-stone-300 outline-none focus:border-amber-400/30"
            value={marketFilter}
            onChange={(e) => setMarketFilter(e.target.value)}
          >
            <option value="">All Markets</option>
            {ALL_MARKETS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stone-600" />
        </div>

        {(search || typeFilter || marketFilter) && (
          <button
            onClick={() => { setSearch(''); setTypeFilter(''); setMarketFilter('') }}
            className="flex items-center gap-1 rounded-md border border-amber-400/15 bg-amber-400/[0.06] px-2 py-1 text-[10px] text-amber-300 hover:bg-amber-400/[0.12]"
          >
            <X size={10} /> Clear
          </button>
        )}

        <div className="ml-auto text-[11px] text-stone-500">
          {filtered.length} of {totalComps} comps
        </div>
      </section>

      {/* Main Table + Slide-out Detail */}
      <div className="flex min-h-0 flex-1 gap-2 overflow-hidden">
        {/* Table */}
        <section className={`card flex min-h-0 flex-col overflow-hidden transition-all duration-300 ${selectedComp ? 'flex-1' : 'w-full'}`}>
          <div className="border-b border-amber-400/10 px-4 py-3">
            <div className="flex items-center gap-2">
              <Database size={14} className="text-amber-400" />
              <span className="terminal-kicker">Comparable Transactions</span>
            </div>
            <p className="mt-1 text-xs text-stone-500">Click any row for detailed property view. Sort by clicking column headers.</p>
          </div>
          <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <SortHeader label="Property" field="property" />
                  <SortHeader label="Market" field="market" />
                  <SortHeader label="Type" field="type" />
                  <SortHeader label="Size" field="sf" />
                  <SortHeader label="Sale Price" field="salePrice" />
                  <SortHeader label="$/SF or $/Unit" field="pricePerSf" />
                  <SortHeader label="Cap Rate" field="capRate" />
                  <SortHeader label="NOI" field="noi" />
                  <SortHeader label="Buyer" field="buyer" />
                  <SortHeader label="Seller" field="seller" />
                  <SortHeader label="Date" field="date" />
                  <SortHeader label="Source" field="source" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-[11px] text-stone-500">
                      {!demoEnabled && compsQuery.isLoading
                        ? 'Loading comps…'
                        : !demoEnabled && compsQuery.isError
                          ? 'The comps store could not be loaded. Nothing is shown rather than a sample.'
                          : totalComps === 0
                            ? 'No comps recorded yet. Comps are added from sale-comp tables in sponsor and broker packages; no market-data vendor (CoStar, RCA) is connected.'
                            : 'No comps match these filters.'}
                    </td>
                  </tr>
                )}
                {filtered.map((comp) => (
                  <tr
                    key={comp.id}
                    className={`cursor-pointer border-b border-amber-400/6 transition-colors hover:bg-amber-400/[0.04] ${selectedComp?.id === comp.id ? 'bg-amber-400/[0.08]' : ''}`}
                    onClick={() => setSelectedComp(selectedComp?.id === comp.id ? null : comp)}
                  >
                    <td className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-amber-200">{comp.property}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-xs text-stone-300">{comp.market}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span className="rounded-full border border-amber-400/15 bg-amber-400/[0.06] px-2 py-0.5 text-[10px] text-amber-200">{comp.type}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">
                      {comp.units ? `${fmtNum(comp.units)} units` : comp.sf ? `${fmtNum(comp.sf)} SF` : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-100">{fmt$(comp.salePrice)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">
                      {/* A unit-counted comp is priced per unit, matching its size column. */}
                      {comp.units && comp.pricePerUnit
                        ? `$${fmtNum(comp.pricePerUnit)}/Unit`
                        : comp.pricePerSf ? `$${fmtNum(comp.pricePerSf)}/SF` : comp.pricePerUnit ? `$${fmtNum(comp.pricePerUnit)}/Unit` : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-emerald-400">{fmtPct(comp.capRate)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">{fmt$(comp.noi)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-xs text-stone-400">{comp.buyer ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-xs text-stone-400">{comp.seller ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-400">{comp.date}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${
                        comp.source === 'Internal'
                          ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300'
                          : comp.source === 'CoStar'
                            ? 'border-blue-500/25 bg-blue-500/10 text-blue-300'
                            : comp.source === 'RCA'
                              ? 'border-violet-500/25 bg-violet-500/10 text-violet-300'
                              : 'border-amber-400/25 bg-amber-400/10 text-amber-200'
                      }`}>
                        {comp.source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Slide-out Detail Panel */}
        {selectedComp && (
          <aside className="card w-[380px] flex-shrink-0 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div className="border-b border-amber-400/10 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="terminal-kicker">Comp Detail</div>
                <button onClick={() => setSelectedComp(null)} className="rounded p-1 text-stone-600 hover:text-amber-300">
                  <X size={14} />
                </button>
              </div>
              <h3 className="mt-2 text-sm font-semibold text-stone-100">{selectedComp.property}</h3>
              <div className="mt-1 flex items-center gap-1 text-[11px] text-stone-500">
                <MapPin size={10} />
                {selectedComp.address}
              </div>
            </div>

            <div className="space-y-3 p-4">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { l: 'Sale Price', v: fmt$(selectedComp.salePrice) },
                  { l: 'Cap Rate', v: fmtPct(selectedComp.capRate) },
                  ((perUnit) => ({ l: perUnit ? '$/Unit' : '$/SF', v: perUnit ? `$${fmtNum(selectedComp.pricePerUnit ?? 0)}` : selectedComp.pricePerSf ? `$${fmtNum(selectedComp.pricePerSf)}` : '-' }))(
                    Boolean(selectedComp.pricePerUnit && (selectedComp.units || !selectedComp.pricePerSf)),
                  ),
                  { l: 'NOI', v: fmt$(selectedComp.noi) },
                  { l: 'Occupancy', v: selectedComp.occupancy == null ? '—' : `${selectedComp.occupancy.toFixed(1)}%` },
                  { l: 'Year Built', v: selectedComp.yearBuilt == null ? '—' : String(selectedComp.yearBuilt) },
                ].map((item) => (
                  <div key={item.l} className="metric-cell">
                    <div className="metric-cell-label">{item.l}</div>
                    <div className="mt-1 text-sm font-semibold text-stone-100">{item.v}</div>
                  </div>
                ))}
              </div>

              {/* Property Details */}
              <div className="rounded-lg border border-amber-400/8 bg-white/[0.015] p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-stone-600">Property Details</div>
                <div className="mt-2 space-y-1.5">
                  {[
                    { l: 'Type', v: selectedComp.type },
                    { l: 'Market / MSA', v: `${selectedComp.market} / ${selectedComp.msa}` },
                    { l: 'Size', v: selectedComp.units ? `${fmtNum(selectedComp.units)} units${selectedComp.sf ? ` / ${fmtNum(selectedComp.sf)} SF` : ''}` : selectedComp.sf ? `${fmtNum(selectedComp.sf)} SF` : '—' },
                    { l: 'Stories', v: selectedComp.stories == null ? '—' : String(selectedComp.stories) },
                    { l: 'Parking', v: selectedComp.parkingSpaces ? fmtNum(selectedComp.parkingSpaces) : 'N/A' },
                    { l: 'Source', v: selectedComp.source },
                    { l: 'Date', v: selectedComp.date },
                  ].map((item) => (
                    <div key={item.l} className="flex justify-between text-xs">
                      <span className="text-stone-500">{item.l}</span>
                      <span className="font-medium text-stone-200">{item.v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buyer / Seller */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-amber-400/8 bg-white/[0.015] p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-stone-600">Buyer</div>
                  <div className="mt-1.5 text-xs font-medium text-stone-100">{selectedComp.buyer ?? 'Not recorded'}</div>
                </div>
                <div className="rounded-lg border border-amber-400/8 bg-white/[0.015] p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-stone-600">Seller</div>
                  <div className="mt-1.5 text-xs font-medium text-stone-100">{selectedComp.seller ?? 'Not recorded'}</div>
                </div>
              </div>

              {/* Notes */}
              <div className="rounded-lg border border-amber-400/8 bg-white/[0.015] p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-stone-600">Notes</div>
                <p className="mt-1.5 text-xs leading-5 text-stone-300">{selectedComp.notes}</p>
              </div>

              {/* Pipeline Link */}
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-emerald-400">
                  <TrendingUp size={11} />
                  Pipeline Matches
                </div>
                <p className="mt-1.5 text-xs text-stone-300">
                  Similar to <span className="font-semibold text-emerald-300">{similarPipelineCount} deal{similarPipelineCount !== 1 ? 's' : ''}</span> in active pipeline
                </p>
                <button className="mt-2 flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300">
                  View matches <ExternalLink size={9} />
                </button>
              </div>

              {/* Map Placeholder */}
              <div className="flex h-36 items-center justify-center rounded-lg border border-amber-400/10 bg-black/30">
                <div className="text-center">
                  <MapPin size={20} className="mx-auto text-stone-600" />
                  <div className="mt-1.5 text-[10px] text-stone-600">Map View</div>
                  <div className="text-[9px] text-stone-700">{selectedComp.market}</div>
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>

      <section className="card flex-shrink-0 overflow-hidden">
        <div className="border-b border-amber-400/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-amber-400" />
            <span className="terminal-kicker">Market Summary by Property Type</span>
          </div>
          <p className="mt-1 text-xs text-stone-500">Aggregate statistics across the comparable transaction database.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {['Property Type', 'Comps', 'Avg Cap Rate', 'Avg $/SF', 'Avg $/Unit', 'YoY Change', 'Transaction Volume'].map((h) => (
                  <th
                    key={h}
                    style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8c8c8c', borderBottom: '1px solid var(--line-soft)', background: 'rgba(0,0,0,0.25)' }}
                    className="px-3 py-1.5 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {marketSummary.map((row) => (
                <tr key={row.type} className="border-b border-amber-400/6 hover:bg-amber-400/[0.04]">
                  <td className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-amber-200">{row.type}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">{row.count}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-emerald-400">{fmtPct(row.avgCapRate)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">{row.avgPriceSf ? `$${Math.round(row.avgPriceSf).toLocaleString()}` : '-'}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-300">{row.avgPriceUnit ? `$${Math.round(row.avgPriceUnit).toLocaleString()}` : '-'}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                    {row.yoyChange == null ? (
                      <span className="text-stone-500">—</span>
                    ) : (
                      <span className={row.yoyChange.startsWith('+') ? 'text-emerald-400' : 'text-red-400'}>{row.yoyChange}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs text-stone-100">{fmt$(row.totalVolume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      )}
    </div>
  )
}
